import React, { useMemo, memo, useState } from 'react';
import { useTranslation } from '~/shared/utils/translation';
import { FilterGroupAttributes } from '../FiltersGroup';
import { SelectedFilters } from '../SelectedFilters';

import FilterIcon from '$icons/filter.svg';
import { CollapsePanel, Typography } from '~/shared/components';
import { TranslationKey } from '~/lib/data-contract';
import { FiltersDrawer } from '../filters-drawer/filters-drawer.component';
import { SortBy } from '../../sort-by/sort-by';
import { useAlgoliaConfig } from '~/shared/hooks/useAlgoliaConfig';
import { connectDynamicWidgets, DynamicWidgetsProvided } from 'react-instantsearch-core';
import { useFilterUIState } from '~/features/productsList/hooks';
import { ResultsCount } from '../results-count/results-count.component';
import styles from './dynamic-filters.module.css';
import FilterTrigger from '../filter-trigger/filter-trigger.component';

type CustomDynamicFiltersProps = DynamicWidgetsProvided;

const CustomDynamicFilters = memo(({ attributesToRender }: CustomDynamicFiltersProps) => {
    const { filtersCount } = useFilterUIState();
    const { translate } = useTranslation();
    const { orderIndexes } = useAlgoliaConfig();
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
    const [filtersModalIsOpen, setFiltersModalIsOpen] = useState(false);

    const filters = useMemo(() => {
        return attributesToRender.map((filter) => {
            return {
                name: filter,
                title: `commerce.filters.${filter}`,
            };
        });
    }, [attributesToRender]);

    // useEffect(() => {
    //     if (isBrowser) {
    //         const handleScroll = () => {
    //             setScrollY(window.scrollY);
    //         };
    //         const debounceHandleScroll = debounce(handleScroll, 100);

    //         handleScroll();

    //         window.addEventListener('scroll', debounceHandleScroll);
    //         return () => {
    //             window.removeEventListener('scroll', debounceHandleScroll);
    //         };
    //     }
    // }, []);

    return (
        <>
            <div className={styles.filters}>
                <div className={styles.filterOptions}>
                    {filters.slice(0, 5).map((filter: { name: string; title: string }) => {
                        return (
                            <FilterTrigger
                                key={filter.name}
                                label={translate(filter.title as TranslationKey)}
                                isOpen={selectedGroup === filter.name}
                                onClick={() => {
                                    if (selectedGroup === filter.name) {
                                        setSelectedGroup(null);
                                    } else {
                                        setSelectedGroup(filter.name);
                                    }
                                }}
                            />
                        );
                    })}
                </div>

                <div className={styles.filterControls}>
                    <button
                        className={styles.allFiltersButton}
                        onClick={() => {
                            setFiltersModalIsOpen(true);
                            setSelectedGroup(null);
                        }}
                    >
                        <Typography as="span" variant="bodySm">
                            {translate('commerce.filters.viewAll')}
                        </Typography>
                        <FilterIcon aria-hidden="true" />
                        {filtersCount ? (
                            <Typography as="span" variant="bodySm" className={styles.filtersCountMobile}>
                                ({filtersCount})
                            </Typography>
                        ): null}
                    </button>
                    <SortBy
                        defaultRefinement={orderIndexes[0].key}
                        items={orderIndexes}
                    />
                </div>
            </div>

            <CollapsePanel isOpen={selectedGroup !== null}>
                <div className={styles.filterAttributes}>
                    {filters.map(({ name }) => (
                        <div
                            style={{ display: selectedGroup === name ? 'block' : 'none' }}
                            key={name}
                        >
                            <FilterGroupAttributes
                                key={name}
                                attribute={name}
                                showMore
                                showMoreLimit={100}
                            />
                        </div>
                    ))}
                </div>
            </CollapsePanel>

            <div className={styles.selectedFiltersWrapper}>
                <SelectedFilters />
                <ResultsCount spacing="header" />
            </div>

            <FiltersDrawer
                isOpen={filtersModalIsOpen}
                onClose={() => {
                    setFiltersModalIsOpen(false);
                }}
            />
        </>
    );
});

const DynamicFilters = connectDynamicWidgets<CustomDynamicFiltersProps>(CustomDynamicFilters);

export default DynamicFilters;
